<script>
import api from '@/api'
import { alertSuccess, alertConfirm } from '@/helpers/swal'
import pagination from '@/components/pagination.vue'

export default {
  data() {
    return {
      loading: true,
      pagetitle: 'Rules',
      categories: [],
      rules: [],
      baseUrl: `${window.apiUrl}/alert-notification-rules/rule-list`,
      totalItems: 0,
      totalPages: 1,
      currentPage: 0,
      perPage: 10,
      sortBy: 'id',
      sortDir: 'desc',
      columns: [
        { name: 'alertLevelCurrent', label: 'Level', sortable: true },
        { name: 'measurement', label: 'Type', sortable: true },
        { name: 'alertMessage', label: 'Message', sortable: true },
        { name: 'time', label: 'Time', sortable: true },
        { name: 'sysName', label: 'Device', sortable: true },
        { name: 'category', label: 'Category', sortable: true },
      ],
    }
  },
  components: { pagination },
  async mounted() {
    await this.getCategories()
    await this.getRules()
  },
  methods: {
    async getRules() {
      this.loading = true
      const params = {
        page: this.currentPage,
        size: this.perPage,
        sortBy: this.sortBy,
        sortDir: this.sortDir,
      }
      await api
        .get(this.baseUrl, { params })
        .then((response) => {
          this.rules = response.data?.rules || []
          this.totalItems = response.data.totalItems
          this.totalPages = response.data.totalPages
          this.currentPage = response.data.currentPage
        })
        .finally(() => {
          this.loading = false
        })
    },

    async getCategories() {
      await api
        .get(`${window.apiUrl}/category/getCategories`)
        .then((response) => {
          this.categories = response.data
        })
        .finally(() => {
          this.loading = false
        })
    },

    getCategoryName(categoryId) {
      let category = this.categories.find((category) => category.id === parseInt(categoryId))
      return category ? category.categoryName : ''
    },

    getComponentName(measurement) {
      let type = ''
      if (measurement === 'monitoring_alert') {
        type = 'Device'
      } else if (measurement === 'memory_alerts') {
        type = 'Memory'
      } else if (measurement === 'memory_alerts_collectd') {
        type = 'Memory EZELINK'
      } else if (measurement === 'disk_alerts') {
        type = 'Disk'
      } else if (measurement === 'disk_alerts_collectd') {
        type = 'Disk EZELINK'
      } else if (measurement === 'cpu_alerts') {
        type = 'CPU'
      } else if (measurement === 'cpu_alerts_collectd') {
        type = 'CPU EZELINK'
      } else if (measurement === 'sync_status_alert') {
        type = 'Sync Status'
      } else if (measurement === 'ha_status_alert') {
        type = 'HA Status'
      }
      return type
    },

    deleteRule(id) {
      alertConfirm('You want to delete this rule').then((result) => {
        if (result.value) {
          api.post(`/alert-notification-rules/delete-rule/${id}`).then(() => {
            alertSuccess('Rule deleted successfully')
            this.getRules()
          })
        }
      })
    },

    sortByColumn(column) {
      if (column.sortable) {
        this.sortBy = column.name
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        this.currentPage = 0
        this.getRules()
      }
    },

    updatePage(newPage) {
      this.currentPage = newPage - 1
      this.getRules()
    },

    showActionColumn() {
      return this.authUserHasPermission('alert_rules_edit') || this.authUserHasPermission('alert_rules_delete')
    },
  },
}
</script>

<template>
  <Teleport to="#elePageTitle">
    {{ pagetitle }}
  </Teleport>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header">
          <div class="row g-3 align-items-center">
            <div class="col-lg-4 col-md-6">
              <!-- <div class="input-group">
                <input type="search" class="form-control" placeholder="Search here" />
                <span class="input-group-text cursor-pointer">
                  <i class="bx bx-search"></i>
                </span>
              </div> -->
            </div>
            <div class="col-md-6 col-lg">
              <div class="d-flex justify-content-end">
                <router-link v-if="authUserHasPermission('alert_rules_add')" to="/alert/rules/create" class="btn btn-primary d-flex align-items-center gap-1">
                  <i class="bx bx-plus align-middle"></i>
                  Add New
                </router-link>
              </div>
            </div>
          </div>
          <!-- end row -->
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-lg-12">
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Categories</th>
                      <th>Alert Levels</th>
                      <th>Components</th>
                      <th>Notify via Email</th>
                      <th>Notify via Webhook</th>
                      <!-- <th>Notify via SMS</th> -->
                      <th v-if="showActionColumn()">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="loading">
                      <tr v-for="n in 5" :key="n">
                        <td v-for="m in showActionColumn() ? 7 : 6" :key="m" class="placeholder-glow">
                          <span class="placeholder col-12"></span>
                        </td>
                      </tr>
                    </template>
                    <template v-else-if="rules.length">
                      <tr v-for="(rule, ruleIndex) in rules" :key="ruleIndex">
                        <td>{{ rule.ruleName }}</td>
                        <td>
                          <div v-for="(categoryId, categoryIndex) in rule.categories" :key="categoryIndex">
                            <span class="badge bg-primary">{{ getCategoryName(categoryId) }}</span>
                          </div>
                        </td>
                        <td>
                          <div v-for="(alertLevel, alertLevelIndex) in rule.alertLevels" :key="alertLevelIndex">
                            <span v-if="alertLevel === 'Critical'" class="badge bg-danger text-uppercase">
                              <i class="mdi mdi-alert-rhombus"></i>
                              {{ alertLevel }}
                            </span>
                            <span v-else-if="alertLevel === 'Warning'" class="badge bg-warning text-uppercase">
                              <i class="mdi mdi-alert-circle"></i>
                              {{ alertLevel }}
                            </span>
                            <span v-if="alertLevel === 'OK'" class="badge bg-success text-uppercase">
                              <i class="mdi mdi-check-circle"></i>
                              {{ alertLevel }}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div v-for="(component, componentIndex) in rule.components" :key="componentIndex">
                            <span class="badge bg-dark">{{ getComponentName(component) }}</span>
                          </div>
                        </td>
                        <td>
                          <span v-if="rule.channelTypes.includes('EMAIL')" class="badge bg-success">YES</span>
                          <span v-else class="badge bg-danger">NO</span>
                        </td>
                        <td>
                          <span v-if="rule.channelTypes.includes('WEBHOOK')" class="badge bg-success">YES</span>
                          <span v-else class="badge bg-danger">NO</span>
                        </td>
                        <!-- <td>
                          <span v-if="rule.channelTypes.includes('SMS')" class="badge bg-success">YES</span>
                          <span v-else class="badge bg-danger">NO</span>
                        </td> -->
                        <td v-if="showActionColumn()">
                          <div class="d-flex gap-2">
                            <router-link
                              v-if="authUserHasPermission('alert_rules_edit')"
                              :to="'/alert/rules/' + rule.id + '/edit'"
                              class="btn btn-primary bg-gradient btn-sm"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Edit"
                            >
                              <eva-icon name="edit" data-eva-height="14" data-eva-width="14" class="fill-white align-text-top"></eva-icon>
                            </router-link>
                            <button
                              v-if="authUserHasPermission('alert_rules_delete')"
                              type="button"
                              class="btn btn-danger bg-gradient btn-sm"
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Delete"
                              @click="deleteRule(rule.id)"
                            >
                              <eva-icon name="trash-2" data-eva-height="14" data-eva-width="14" class="fill-white align-text-top"></eva-icon>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr>
                        <td colspan="7" class="text-center">No rules found</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- end row -->
        </div>
        <div v-if="!loading && rules.length" class="card-footer">
          <pagination :baseUrl="baseUrl" :totalItems="totalItems" :perPage="perPage" :currentPage="currentPage + 1" @page-changed="updatePage" />
        </div>
      </div>
    </div>
  </div>
</template>
